import { gql } from 'apollo-boost';
import ExpandCardsWithFetch from 'core/components/common/ExpandCardsWithFetch';
import { useLinks } from 'core/components/linksContext';
import { useFiltersFromUrl } from 'core/hooks/useFiltersFromUrl';
import useToggle from 'core/hooks/useToggle';
import { button, column, row, title } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { isMobile } from 'core/utils';
import SearchEventManager from 'core/utils/EventManager';
import React, { useEffect } from 'react';
import { useGetPurchasedProductsQuery } from 'site/graphql';
import { IRefreshAfterArchiveOrDelete } from './AccountCourses';
import ProductCard from './ProductCard';
import ProductListItem from './ProductListItem';

gql`
    query GetPurchasedProducts($filter: ProductListRequest) {
        purchasedProducts(filter: $filter) {
            count
            result {
                id
                name
                shortDescription
                lastPurchasedDate
                isFree
                isArchive
                lecturers {
                    id
                    account {
                        id
                        firstName
                        lastName
                    }
                }
            }
        }
    }
`;

type IPurchasedProductsProps = {
    isArchiveProducts: boolean;
    searchEventManager: SearchEventManager;
    refreshAfterArchiveOrDelete: IRefreshAfterArchiveOrDelete[];
} & WithStyles<typeof styles>;

const PurchasedProducts: React.FC<IPurchasedProductsProps> = ({
    classes,
    isArchiveProducts,
    refreshAfterArchiveOrDelete,
    searchEventManager,
}) => {
    const { search } = useFiltersFromUrl();
    const [toggle, { switchToggle }] = useToggle();
    const links = useLinks();

    const { data, refetch, loading } = useGetPurchasedProductsQuery({
        fetchPolicy: 'network-only',
        variables: {
            filter: {
                search,
                isArchive: isArchiveProducts,
                take: 3,
            },
        },
    });

    const products = (data && data.purchasedProducts && data.purchasedProducts.result) || [];
    const count = (data && data.purchasedProducts && data.purchasedProducts.count) || 0;

    useEffect(() => {
        searchEventManager.subscribe((search: string) => {
            refetch({ filter: { search, isArchive: isArchiveProducts, take: 3 } });
        });

        refreshAfterArchiveOrDelete.push(search =>
            refetch({ filter: { search, isArchive: isArchiveProducts, full: true } }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <div className={classes.header}>
                <h2 className={classes.title}>{isArchiveProducts ? 'Архив' : 'Мои курсы'}</h2>
                {!isMobile() && (
                    <a onClick={switchToggle} className={classes.productModeSwitcher}>
                        {toggle ? 'Показать плитками' : 'Показать списком'}
                    </a>
                )}
            </div>

            {products.length === 0 ? (
                <div>
                    Здесь пока ничего нет.{' '}
                    <links.courseList.Link to={links.courseList.url()}>Перейти к выбору курса</links.courseList.Link>
                </div>
            ) : (
                <ExpandCardsWithFetch
                    classes={{ root: classes.root, button: classes.button }}
                    items={products}
                    count={count}
                    collapseCount={3}
                    isFetching={loading}
                    fetchOnOpen={() => refetch({ filter: { search, isArchive: isArchiveProducts, full: true } })}
                    linkTitle={'Показать все курсы'}
                >
                    {product =>
                        toggle ? (
                            <ProductListItem
                                product={product}
                                key={product.id}
                                fetchProducts={() => refreshAfterArchiveOrDelete.forEach(cb => cb(search))}
                            />
                        ) : (
                            <div className={classes.item} key={product.id}>
                                <ProductCard
                                    product={product}
                                    isDark={false}
                                    fetchProducts={() => refreshAfterArchiveOrDelete.forEach(cb => cb(search))}
                                />
                            </div>
                        )
                    }
                </ExpandCardsWithFetch>
            )}
        </>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        item: {
            ...column(),
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '1.5rem',
            [theme.breakpoints.up('sm')]: {
                ...column({ cols: 6 }),
            },
            [theme.breakpoints.up('md')]: {
                ...column({ cols: 4 }),
            },
        },
        title: {
            ...title({ size: 'h2', mode: 'left' }),
        },
        header: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
        },
        productModeSwitcher: {
            ...button(theme),
            padding: '0.6rem 2.2rem',
            alignSelf: 'baseline',
            boxShadow: '0px 2px 3px 0 rgba(0, 0, 0, 0.09)',
        },
        root: {
            ...row(),
        },
        button: {
            marginLeft: 15,
            marginBottom: 1.5 + 'rem',

            [theme.breakpoints.down('xs')]: {
                marginLeft: 'auto',
            },
        },
    });

export default injectSheet(styles)(PurchasedProducts);
